import { useProPilotStoreActions } from "PFApp/stores/propilot_store";
import { useSession } from "PFApp/use_session";
import { parseStreamingTimeoutMs } from "PFCore/helpers/parse_streaming_timeout_ms";
import type {
  ChatAttachment,
  ConversationId,
  ProPilotFrame,
  ProPilotMessagePayload,
  ProPilotSocket,
  ProPilotSocketFactory,
  Unsubscribe
} from "PFCore/services/propilot";
import {
  createAtlasProPilotSocket,
  createMockProPilotSocket,
  normalizeIncomingFrame
} from "PFCore/services/propilot";
import { IsoDate } from "PFTypes/date_time";
import { useCallback, useEffect, useRef } from "react";

import { useIsProPilotPermitted } from "./use_is_propilot_permitted";
import { useSessionConversationId } from "./use_session_conversation_id";

const extractReason = (err: unknown): string => {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  if (typeof err === "string" && err.length > 0) {
    return err;
  }
  return "error";
};

// should be the same as the agent name in the atlas_ai repo
const PROPILOT_AGENT_NAME = "propilot";

export const useProPilotSocket = (externalConversationId?: ConversationId) => {
  const isProPilotEnabled = useIsProPilotPermitted();
  const { conversationId: sessionConversationId, rotateConversationId } =
    useSessionConversationId(isProPilotEnabled);
  const conversationId = externalConversationId ?? sessionConversationId;

  const session = useSession();
  const { createConversation, appendMessage, setStatus, setError, setPendingRetry } =
    useProPilotStoreActions();

  const socketRef = useRef<ProPilotSocket | null>(null);
  const connectedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const timeoutMs = parseStreamingTimeoutMs(window.PF?.config?.propilot_streaming_timeout_ms);

  const clearStreamingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const armStreamingTimeout = useCallback(() => {
    clearStreamingTimeout();
    timeoutRef.current = setTimeout(() => {
      setError(conversationId, "timeout");
      timeoutRef.current = null;
    }, timeoutMs);
  }, [clearStreamingTimeout, conversationId, setError, timeoutMs]);

  useEffect(() => {
    if (!isProPilotEnabled) {
      return;
    }

    let active = true;

    const factory: ProPilotSocketFactory =
      window.PF.config.propilot_mock === "true" ? createMockProPilotSocket : createAtlasProPilotSocket;

    const socket = factory({ getToken: () => session.getAccessToken() });
    socketRef.current = socket;

    createConversation(conversationId);

    const unsubscribeFrames = socket.onResponse((frame: ProPilotFrame) => {
      const normalized = normalizeIncomingFrame(frame);

      if (normalized.kind === "message") {
        appendMessage(conversationId, {
          id: crypto.randomUUID(),
          author: "agent",
          createdAt: new Date().toISOString() as IsoDate,
          content: normalized.content
        });
        setStatus(conversationId, "streaming");
      } else if (normalized.kind === "done") {
        clearStreamingTimeout();
        setPendingRetry(conversationId, undefined);
        setStatus(conversationId, "idle");
      } else if (normalized.kind === "error") {
        clearStreamingTimeout();
        setError(conversationId, normalized.reason);
      }
    });

    const unsubscribeErrors = socket.onError((reason) => {
      clearStreamingTimeout();
      setError(conversationId, reason);
    });

    void socket
      .connect(conversationId)
      .then(() => {
        if (active) {
          connectedRef.current = true;
        }
      })
      .catch((err) => {
        if (active) {
          setError(conversationId, extractReason(err));
        }
      });

    return () => {
      active = false;
      connectedRef.current = false;
      unsubscribeFrames();
      unsubscribeErrors();
      clearStreamingTimeout();
      socket.close();
      socketRef.current = null;
    };
    // session is treated as stable per project convention; re-running the
    // effect on its identity would tear down the socket unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProPilotEnabled, conversationId]);

  const reply = useCallback(
    (payload: ProPilotMessagePayload): boolean => socketRef.current?.send(payload) ?? false,
    []
  );

  const submitText = useCallback(
    (text: string, attachments?: ChatAttachment[]): boolean =>
      reply({
        type: "user_message",
        agent: PROPILOT_AGENT_NAME,
        message: text,
        ...(attachments && attachments.length > 0 ? { attachments } : {})
      }),
    [reply]
  );

  const subscribe = useCallback(
    (callback: (frame: ProPilotFrame) => void): Unsubscribe =>
      socketRef.current?.onResponse(callback) ?? (() => {}),
    []
  );

  const isConnected = useCallback(() => connectedRef.current, []);

  return {
    conversationId,
    reply,
    submitText,
    subscribe,
    armStreamingTimeout,
    isConnected,
    rotateConversationId
  };
};
